import { ProjectContext } from './../../interfaces/project';
import { Component, Input, SimpleChanges, Output, OnInit, OnChanges } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'angular-2-local-storage';

import { Mediaclip } from '../../interfaces/mediaclip';
import { Playlist } from '../../interfaces/playlist';
import { Channel } from '../../interfaces/channel';
import { Project } from '../../interfaces/project';
import { PlayoutSubset, Playout } from '../../interfaces/playout';
import { Publication } from '../../interfaces/publication';
import { SapiService } from '../../services/sapi.service';
import { OverlayService } from '../../services/overlay.service';
import { ClipboardService } from 'ngx-clipboard';
import { PublicationService } from '../../services/publication.service';
import { BbSelectOption } from '../shared/bb-select/bb-select.component';

import { Subscription } from 'rxjs';
import { EventEmitter } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';

export interface EmbedPreviewConfiguration {
	autoPlay?: boolean;
	standAlone?: boolean;
	alwaysShow?: boolean;
	showVideo?: boolean;
}

@Component({
	selector: 'embed-preview',
	templateUrl: './embed-preview.component.html',
	styleUrls: ['./embed-preview.component.scss']
})

export class EmbedPreviewComponent implements OnInit, OnChanges {
	@Input() mediaclip: Mediaclip;
	@Input() project: Project;
	@Input() channel: Channel;
	@Input() configuration: EmbedPreviewConfiguration;
	@Input() playlist: Playlist;
	@Input() inModal: boolean;
	@Input() form: NgForm;
	@Input() assetId: string;
	@Input() contexts: ProjectContext[];
	@Input() showEmbedTag = true;
	@Input() isActive = true;
	@Input() theme: 'light-v2' | undefined;

	@Output() onPlayoutChange: EventEmitter<PlayoutSubset>;

	contextOptions: BbSelectOption[];
	context: string;

	embed = false;
	embedCode: string;
	haveProjectRelation = false;
	viewpageUrl: string;
	projectViewpageUrl: string;

	previewPlayout: Playout;
	previewPlayoutCode: string;
	selectedEmbedType = 'javascript';
	embedTypeOptions: BbSelectOption[];

	lastSelectedPlayoutId: string;
	relations: any = {};
	type: string;

	// channel
	ctype: null;
	cid: null;
	contentTypes: { label: string; value: string }[] = [
		{ label: 'PLAYLIST', value: 'cliplist' },
		{ label: 'MEDIACLIP', value: 'mediaclip' },
		{ label: 'PROJECT', value: 'project' }
	];

	noLinkJavascript: SafeUrl;
	demoMediaclip: Mediaclip;
	private subscriptions = new Subscription();

	constructor(
		private sapiService: SapiService,
		private overlayService: OverlayService,
		private localStorageService: LocalStorageService,
		private clipboard: ClipboardService,
		private publicationService: PublicationService,
		private translate: TranslateService,
		private domSanitizer: DomSanitizer,
		private http: HttpClient
	) {
		this.noLinkJavascript = this.domSanitizer.bypassSecurityTrustUrl(`javascript:void(0)`);
		this.demoMediaclip = { id: 'ovp2575390' };
		this.onPlayoutChange = new EventEmitter<PlayoutSubset>();
	}

	ngOnInit() {
		// configuration defaults
		if (!this.configuration) {
			this.configuration = {};
		}
		if (typeof this.configuration.autoPlay === 'undefined') {
			this.configuration.autoPlay = true;
		}

		if (typeof this.configuration.showVideo === 'undefined') {
			this.configuration.showVideo = true;
		}

		this.embedTypeOptions = [
			{ label: 'javascript', value: 'javascript' },
			{ label: 'iframe', value: 'iframe' },
			{ label: 'AMP', value: 'amp' }
		];

		if (this.project || this.channel || this.mediaclip || this.playlist) {
			this.embedTypeOptions.push({ label: 'URL', value: 'url' });
			this.embedTypeOptions.push({ label: 'oEmbed', value: 'oembed' });
		}
		if (this.mediaclip || this.project) {
			this.embedTypeOptions.push({ label: 'SCORM', value: 'scorm' });
		}
		this.getProjectMainClip();
		this.getRelations(this.mediaclip);
		this.determineInitialPlayout();

		if (this.configuration) {
			this.handleConfig(this.configuration);
		}

		this.type = (this.playlist ? 'playlist' : 'mediaclip');

		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			if (!publication) {
				return;
			}

			// show dummy content when alwaysShow and no valid video content provided
			if (this.configuration.alwaysShow) {
				if (publication.baseurl) {
					this.http.get(publication.baseurl + '/p/default/c/' + this.demoMediaclip.id + '.json')
						.subscribe((response: any) => {
							const clipData = response.clipData;
							if (clipData) {
								this.demoMediaclip.assets = clipData.assets || [];
								this.demoMediaclip.mediatype = clipData.mediatype;
							}
							if (!this.mediaclip && (this.project && !this.project.mainMediaClipId)) {
								this.mediaclip = this.demoMediaclip;
							}
						});
				}
			}
		}));
	}

	ngOnChanges(changes: SimpleChanges) {
		for (const propName in changes) {
			if (typeof changes[propName] !== 'undefined' && !changes[propName].firstChange) {
				if (changes[propName].currentValue) {
					if ((propName === 'project' || propName === 'channel') && changes[propName].currentValue.id &&
						changes[propName].currentValue.id !== changes[propName].previousValue.id) {
						this.determineEmbedCode();
						if (propName === 'project' || propName === 'channel') {
							this.determineViewPageUrl();
						}
					}
					if (propName === 'mediaclip') {
						this.getRelations(changes.mediaclip.currentValue);
					}
				}
			}
		}

		if (changes.project?.currentValue?.contexts?.length) {
			this.contextOptions = changes.project.currentValue.contexts.map((o) => ({
				label: o.label,
				value: o.code
			}));
			this.contextOptions.unshift({
				label: 'Default (no context)',
				value: ''
			});
		}
	}

	getRelations(clip: Mediaclip) {
		if (clip && clip.relationLabel && clip.relationLabel.length) {
			clip.relationLabel.forEach((label) => {
				if (label && label.indexOf('Project') !== -1 && !this.relations[label]) {
					this.haveProjectRelation = true;
					const id = label.split('Project')[1];
					this.relations[label] = {
						id,
						entityType: 'Project'
					};
					this.sapiService.get('project', id).subscribe((project: any) => {
						this.relations[label].label = project.name || project.title;
					});
				}
			});
		}
	}

	// for a project: lookup the main mediaclip to show in the player
	getProjectMainClip(): void {
		if (!this.project || !this.project.mainMediaClipId || this.mediaclip) {
			return;
		}
		this.sapiService.get('mediaclip', this.project.mainMediaClipId).subscribe((clip: Mediaclip) => {
			this.mediaclip = clip;
		});
	}

	ngDestroy() {
		this.subscriptions.unsubscribe();
	}

	embedToggled(): void {
		this.determineInitialPlayout();
	}

	copyEmbedCode() {
		this.clipboard.copyFromContent(this.embedCode);
		this.overlayService.createToast(this.translate.instant('EMBED_CODE_COPIED_TO_CLIPBOARD'), 'copy');
	}

	textareaClicked(event: MouseEvent): void {
		(event.target as any).select();
	}

	embedCodeTypeChanged(): void {
		this.determineEmbedCode();
	}

	onPlayoutSelected(previewPlayoutCode: string) {
		this.selectPlayoutById(previewPlayoutCode);
	}

	selectPlayoutById(previewPlayoutCode: string) {
		if (this.configuration) {
			this.sapiService.get('playout', previewPlayoutCode).subscribe((playout: Playout) => {
				this.previewPlayout = playout;
				this.previewPlayoutCode = playout.label;
				this.localStorageService.set('ovp-lastSelectedPlayoutId', playout.id);
				this.onPlayoutChange.emit(this.previewPlayout);
				this.updatePlaceholder(this.previewPlayout);
				this.determineEmbedCode();
				this.determineViewPageUrl();
			});
		}
	}

	onContextChanged() {
		this.determineEmbedCode();
		this.determineViewPageUrl();
	}

	channelCidChanged() {
		this.determineEmbedCode();
		this.determineViewPageUrl();
	}

	private handleConfig(configuration: EmbedPreviewConfiguration): void {
		if (configuration.standAlone) {
			this.embed = true;
		}
	}

	private determineInitialPlayout(): void {

		// attempt #1: previous selection from local storage
		const previousPlayoutId: any = this.localStorageService.get('ovp-lastSelectedPlayoutId');

		if (previousPlayoutId) {
			this.selectPlayoutById(previousPlayoutId);
			return;
		}

		// attempt #2: "main" = true /  "name" = 'default' / recently created
		this.sapiService.query('playout', [{
			name: 'q',
			values: ['main:true^100 OR label:"default"^50 OR createddate:[NOW-3MONTH TO NOW]^10 OR *:*']
		}], 0, 1, 'score', 'desc').subscribe((result) => {
			if (result?.items && result?.items[0]?.id) {
				this.selectPlayoutById(result.items[0].id);
			}
		});
	}

	private determineViewPageUrl(): void {
		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			if (!publication) {
				return;
			}

			if (this.mediaclip && !this.project) {
				if (publication.externalBaseUrl) {
					this.viewpageUrl = '//' + publication.externalBaseUrl;
				} else if (publication.baseurl) {
					this.viewpageUrl = publication.baseurl;
				}
				this.viewpageUrl += '/view/' + this.previewPlayout.label + '/' + this.mediaclip.id + '.html?placementOption=default';
				return;
			}

			if (!publication.baseurl) {
				return;
			}

			if (this.project?.id) {
				this.projectViewpageUrl = publication.baseurl + '/p/' + this.previewPlayout.label + '/p/' + this.project.id
					+ '.html?inheritDimensions=true&noStats=true&placementOption=default'
					+ (this.context ? `&context=${encodeURIComponent(this.context)}` : '');
				return;
			};

			if (this.channel?.id) {
				this.projectViewpageUrl = publication.baseurl + '/ch/' + this.channel.id + '.html?noStats=true';
				if (this.cid && this.ctype) {
					this.projectViewpageUrl += `&cid=${this.cid}&ctype=${this.ctype}`;
				}
				return;
			}

			if (this.playlist?.id) {
				this.viewpageUrl = publication.baseurl + '/p/' + this.previewPlayout.label + '/l/' + this.playlist.id
					+ '.html?inheritDimensions=true&noStats=true&placementOption=default';
				return;
			}
		}));
	}

	private updatePlaceholder(playout: Playout): void {
		if (this.configuration.alwaysShow) {
			if (playout && playout.format) {
				const format = playout.format;
				switch (format) {
					case '21:9':
						this.demoMediaclip.id = 'ovp2575388';
						break;
					case '4:3':
						this.demoMediaclip.id = 'ovp2575387';
						break;
					default:
						this.demoMediaclip.id = 'ovp1082419';
				}
			}
		}
	}

	private determineEmbedCode() {
		let url = '/embedcode/';

		if (this.playlist) {
			url += 'l/' + this.playlist.id;
		} else if (this.project && this.project.id) {
			url += 'p/' + this.project.id;
		} else if (this.mediaclip && this.mediaclip.id && !this.mediaclip.id.startsWith('ovp')) {
			url += this.mediaclip.id;
		} else if (this.channel && this.channel.id) {
			url += 'ch/' + this.channel.id;
		} else {
			// console.warn( 'No playlist/project/mediaclip was set, skip retrieving embed url' );
			return;
		}

		const playoutCode = this.previewPlayout?.label || 'default';

		url += '/' + playoutCode + '/' + this.selectedEmbedType;

		if (this.context) {
			url += `?context=${encodeURIComponent(this.context)}`;
		}

		if (this.channel && this.cid && this.ctype) {
			url += `?cid=${this.cid}&ctype=${this.ctype}`;
		}

		this.subscriptions.add(this.sapiService.getUrl(url).subscribe(data => {
			this.embedCode = data.body;
		}));
	}
}
